/**
 * Ein Knopf, der nur ein Symbol trägt.
 *
 * Übernommen aus `components/forms/IconButton.jsx`. `label` ist hier **Pflicht**
 * und nicht optional: Ein Knopf ohne Beschriftung wird von einer Sprachausgabe
 * als „Schaltfläche" angesagt und ist damit unbenutzbar. Die Symbole selbst
 * tragen `aria-hidden` (siehe `icons.tsx`), also ist dies die einzige Stelle,
 * an der die Bedeutung stehen kann.
 *
 * **Zur Größe:** Beide Stufen liegen unter den 44 pt, die das Design-System als
 * Berührziel fordert. Das ist für die Maus in Ordnung und für den Finger nicht —
 * die Forderung gilt der Mobile-App, und die bekommt mit HM-013 eigene Maße.
 * Wer diesen Knopf hier für eine allein stehende Aktion benutzt, nimmt `md`;
 * `sm` gehört in eine dichte Leiste, in der die Nachbarn dasselbe tun.
 */
import { useState, type ButtonHTMLAttributes, type CSSProperties, type ReactNode } from "react";

export type IconButtonVariant = "ghost" | "secondary";
export type IconButtonSize = "sm" | "md";

/**
 * **`HTMLElement` und nicht `HTMLButtonElement`** — seit es `href` gibt, ist das
 * gerenderte Element eines von beiden, und ein `onClick`, der auf ein
 * `HTMLButtonElement` typisiert ist, passt dann auf das `<a>` nicht. Über die
 * gemeinsame Basis gilt er für beide.
 */
export interface IconButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLElement>, "type" | "aria-label"> {
  /** Was der Knopf tut — „Vorheriger Monat". Wird zur `aria-label`. */
  readonly label: string;
  readonly children: ReactNode;
  readonly variant?: IconButtonVariant;
  readonly size?: IconButtonSize;
  /**
   * Ziel als Adresse. Mit ihr wird aus dem Knopf ein `<a>` — gleiches Aussehen,
   * gleiche Maße, aber kopierbar und im neuen Tab zu öffnen.
   *
   * **Dieselbe Weiche wie in `NavItem`** und aus demselben Grund (HM-038): Wo
   * es wirklich eine Adresse gibt, ist ein `<button>` davor ein Versprechen zu
   * wenig. Wo es keine gibt — „Vorheriger Monat" —, bleibt es ein Knopf; ein
   * `<a href="#">` verspräche ein Ziel, das es nicht gibt.
   *
   * `onClick` gilt dann nur dem gewöhnlichen Klick: Strg-, Cmd- und
   * Mittelklick gehören dem Browser, und der Aufrufer lässt sie durch.
   *
   * **Mit `href` gibt es kein `disabled`.** Einen abgeschalteten Link kennt
   * HTML nicht; was gerade nicht führt, wird weggelassen (Regel 10).
   */
  readonly href?: string;
}

const SIZES: Record<IconButtonSize, number> = { sm: 30, md: 36 };

const VARIANTS: Record<IconButtonVariant, CSSProperties> = {
  ghost: { background: "transparent", color: "var(--text-body)", border: "1px solid transparent" },
  secondary: {
    background: "var(--surface-card)",
    color: "var(--text-body)",
    border: "1px solid var(--border-default)",
  },
};

export function IconButton({
  label,
  children,
  variant = "ghost",
  size = "md",
  disabled = false,
  href,
  style,
  ...rest
}: IconButtonProps) {
  const [hovered, setHovered] = useState(false);
  const box = SIZES[size];

  const shape: CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: box,
    height: box,
    flexShrink: 0,
    padding: 0,
    borderRadius: "var(--radius-sm)",
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.45 : 1,
    textDecoration: "none",
    transition: "background .15s ease",
    ...VARIANTS[variant],
    ...(hovered && !disabled ? { background: "var(--surface-sunken)" } : {}),
    ...style,
  };

  const handlers = {
    onMouseEnter: () => setHovered(true),
    onMouseLeave: () => setHovered(false),
  };

  if (href !== undefined) {
    /* `rest` trägt hier nur, was ein `<a>` auch versteht — `onClick` und die
       ARIA-Attribute. `disabled` bleibt draußen: siehe `href`. */
    return (
      <a href={href} aria-label={label} title={label} style={shape} {...handlers} {...rest}>
        {children}
      </a>
    );
  }

  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      style={shape}
      {...handlers}
      {...rest}
    >
      {children}
    </button>
  );
}
